// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import type { Firmware } from "@/api/hooks/use-firmware";
import { DateTimeValue } from "@/components/shared/datetime-value";
import { Separator } from "@/components/ui/separator";
import { FirmwareCpuTypeBadge, FirmwareDeviceTypeBadge } from "@/features/hardware/firmware-badges";

interface FirmwareInfoTabProps {
  version: Firmware;
}

export function FirmwareInfoTab({ version }: FirmwareInfoTabProps) {
  const { t } = useTranslation("back-office");

  return (
    <div className="max-w-2xl space-y-6 p-6">
      <div className="grid grid-cols-[160px_1fr] gap-y-3 text-sm">
        <span className="text-muted-foreground">{t("firmware.fields.versionStr")}</span>
        <span className="font-mono">{version.versionStr}</span>

        <span className="text-muted-foreground">{t("firmware.fields.versionNum")}</span>
        <span className="font-mono tabular-nums">{version.versionNum}</span>

        <span className="text-muted-foreground">{t("firmware.fields.deviceType")}</span>
        <span>
          <FirmwareDeviceTypeBadge deviceType={version.deviceType} />
        </span>

        <span className="text-muted-foreground">{t("firmware.fields.cpuType")}</span>
        <span>
          <FirmwareCpuTypeBadge cpuType={version.cpuType} />
        </span>

        <Separator className="col-span-2" />

        <span className="text-muted-foreground">{t("firmware.fields.fileUri")}</span>
        <span className="font-mono break-all">{version.fileUri}</span>

        <span className="text-muted-foreground">{t("firmware.fields.md5")}</span>
        <span className="font-mono break-all">{version.md5 || <span className="text-muted-foreground">—</span>}</span>

        <span className="self-start text-muted-foreground">{t("firmware.fields.changeLog")}</span>
        <span className="whitespace-pre-wrap">
          {version.changeLog || <span className="text-muted-foreground">—</span>}
        </span>

        <Separator className="col-span-2" />

        <span className="text-muted-foreground">{t("firmware.fields.createdAt")}</span>
        <DateTimeValue value={version.createdAt} />

        <span className="text-muted-foreground">{t("firmware.fields.updatedAt")}</span>
        <DateTimeValue value={version.updatedAt} />
      </div>
    </div>
  );
}
